import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Deal } from '@interfaces/deal.interfaces';
import { User, UserTypes } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { takeWhile, debounceTime } from 'rxjs';
import { PhonePipe } from 'src/app/pipes/phone.pipe';
import { namePattern, emailPattern, usPhonePattern, phoneExporter, phoneImporter } from 'src/app/utils/form-utils';
import { formatPhone } from 'src/app/utils/format-util';

@Component({
    selector: 'app-add-cobuyer-modal',
    templateUrl: './add-cobuyer-modal.component.html',
    styleUrls: ['./add-cobuyer-modal.component.scss'],
})
export class AddCobuyerModalComponent implements OnInit, OnDestroy {
    private isComponentAlive = true;
    public form: FormGroup;
    public foundUser: User;

    constructor(
        private readonly fb: FormBuilder,
        private readonly userService: UserService,
        private phonePipe: PhonePipe,
        private dialogRef: MatDialogRef<AddCobuyerModalComponent>,
        @Inject(MAT_DIALOG_DATA) public deal: Deal
    ) {}

    get userIsBuyer(): boolean {
        return this.foundUser && this.foundUser.userId === this.deal?.buyer?.userId;
    }

    get userIsNotCustomer(): boolean {
        return this.foundUser && this.foundUser.userType !== UserTypes.Customer;
    }

    ngOnInit() {
        this.buildForm();
    }

    buildForm() {
        this.form = this.fb.group({
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            middleName: [null, Validators.compose([Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            phone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });

        this.form.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.form.controls.email.setValue(value.trim());

                if (this.form.controls['email'].valid && this.foundUser?.email !== value) {
                    this.findUser(value);
                }
            });
    }

    findUser(email: string) {
        this.userService
            .checkUserByEmail$(email)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                if (user) {
                    this.foundUser = user;

                    // Set the formValues
                    this.form.patchValue({
                        firstName: user.firstName,
                        middleName: user.middleName,
                        lastName: user.lastName,
                        phone: user.phone ? formatPhone(phoneImporter(user.phone)) : undefined,
                    });

                    this.form.controls.firstName.disable();
                    this.form.controls.middleName.disable();
                    this.form.controls.lastName.disable();
                    this.form.controls.phone.disable();
                } else {
                    this.form.controls.firstName.enable();
                    this.form.controls.middleName.enable();
                    this.form.controls.lastName.enable();
                    this.form.controls.phone.enable();

                    if (this.foundUser) {
                        this.form.patchValue({
                            phone: '',
                            firstName: '',
                            middleName: '',
                            lastName: '',
                        });
                        this.foundUser = undefined;
                    }
                }
            });
    }

    addCoBuyer() {
        let cobuyer = {};
        // Need to use rawValue to get disabled fields' values
        const formValue = this.form.getRawValue();
        if (this.form.valid && !this.userIsBuyer && !this.userIsNotCustomer) {
            if (!this.foundUser)
                cobuyer = {
                    ...cobuyer,
                    enableMFA: true,
                };

            this.dialogRef.close({
                valid: true,
                cobuyer: {
                    ...cobuyer,
                    ...formValue,
                    phone: phoneExporter(formValue.phone),
                },
            });
        }
    }

    close() {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
